<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <!-- Header -->
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/dashboard')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-clipboard-pulse me-2"></i>{{ $t('page.registries_hub', 'Disease Registries') }}</h4>
        </div>
      </div>

      <!-- Intro -->
      <div class="hms-card hms-intro">
        <div class="d-flex align-items-start gap-3">
          <i class="mdi mdi-information-outline hms-intro-icon"></i>
          <div>
            <div class="hms-text fw-semibold mb-1">Longitudinal disease management</div>
            <div class="hms-muted-sm">
              Each registry below tracks patients enrolled into a specific chronic-care or notifiable-disease programme.
              Click a registry to enrol new patients, list active cases, and follow-up.
            </div>
          </div>
        </div>
      </div>

      <!-- Registry cards -->
      <div class="hms-grid">
        <div v-for="r in registries" :key="r.type" class="hms-reg-card" @click="$router.push('/registry/' + r.type)" :style="'border-color:' + r.color + '40'">
          <div class="hms-reg-icon" :style="'background:' + r.color + '20; color:' + r.color">
            <i :class="r.icon"></i>
          </div>
          <div class="hms-reg-body">
            <div class="hms-reg-title">{{ r.title }}</div>
            <div class="hms-reg-desc">{{ r.description }}</div>
            <div class="hms-reg-stats">
              <span v-if="counts[r.type] !== undefined" class="hms-reg-count">{{ counts[r.type] }} {{ counts[r.type] === 1 ? 'patient' : 'patients' }}</span>
              <span v-else class="hms-muted-sm">Loading...</span>
            </div>
          </div>
          <i class="mdi mdi-chevron-right hms-reg-chev"></i>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root,
html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;--hms-placeholder:#4a4a4a;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;--hms-placeholder:#aaaaaa;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-accent:#F5821F;--hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;--hms-info:#17a2b8;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; text-decoration: none; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-text { color: var(--hms-h); }

.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 20px; margin-bottom: 16px; }
.hms-intro { display: flex; align-items: center; }
.hms-intro-icon { font-size: 28px; color: var(--hms-primary); }

.hms-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; }
.hms-reg-card {
  background: var(--hms-bg1);
  border: 1px solid var(--hms-br);
  border-radius: 14px;
  padding: 18px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all .15s;
  position: relative;
}
.hms-reg-card:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(0,0,0,.10); }
.hms-reg-icon { width: 56px; height: 56px; border-radius: 14px; display: flex; align-items: center; justify-content: center; font-size: 28px; flex-shrink: 0; }
.hms-reg-body { flex: 1; min-width: 0; }
.hms-reg-title { color: var(--hms-h); font-weight: 700; font-size: 16px; }
.hms-reg-desc { color: var(--hms-m); font-size: 12px; margin-top: 2px; }
.hms-reg-stats { margin-top: 6px; }
.hms-reg-count { color: var(--hms-primary); font-weight: 600; font-size: 13px; }
.hms-reg-chev { color: var(--hms-s); font-size: 22px; }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";
import { REGISTRY_TYPES } from "@/components/hms-registry/registry-config.js";

export default {
  name: "RegistryHub",
  components: { Navigation },
  data() {
    return {
      counts: {},
      registries: REGISTRY_TYPES,
      branchId: sessionStorage.getItem("branchId")
    };
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) {
      this.$router.push("/login");
      return;
    }
    this.applyTheme();
    this.loadCounts();
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    async loadCounts() {
      var self = this;
      // Load patient counts in parallel for all registries
      this.registries.forEach(async (r) => {
        try {
          var res = await executeJson({ function: r.listFn, branch_id: self.branchId }, hms);
          if (res.data && res.data.success) {
            var key = r.dataKey || "Patients";
            var arr = (res.data.data && res.data.data[key]) || [];
            self.counts[r.type] = arr.length;
          } else {
            self.counts[r.type] = 0;
          }
        } catch (e) {
          self.counts[r.type] = 0;
        }
      });
    }
  }
};
</script>
